import React, { useState, useEffect, forwardRef, useImperativeHandle, useCallback, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { GET_PROPERTIES } from '../graphql/queries';
import { GET_LOST_FOUND_RESERVATION_CANDIDATES } from '../graphql/lost-found-queries';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-hot-toast';
import { API_URL } from '../config';
import SafeImage from './SafeImage';

const ITEMS_PER_PAGE = 20;
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const NO_RESERVATION_VALUE = '__NO_RESERVATION__';

export const LOST_FOUND_CATEGORY = {
  LOST_ITEM_FOUND: 'LOST_ITEM_FOUND',
  REPORTED_LOST_ITEM: 'REPORTED_LOST_ITEM',
};

const LostFoundForm = forwardRef(({ onSubmit, isLoading, initialData = {}, hideButtons = false }, ref) => {
  const [category, setCategory] = useState(initialData.category || LOST_FOUND_CATEGORY.LOST_ITEM_FOUND);
  const [description, setDescription] = useState(initialData.description || '');
  const [photos, setPhotos] = useState(initialData.photos || []);
  const [reservationId, setReservationId] = useState(initialData.reservationId || '');
  const [returnOfficeInstructions, setReturnOfficeInstructions] = useState(
    initialData.returnOfficeInstructions || 'Return this item to the correct office.'
  );
  const [errors, setErrors] = useState({});
  const [propertySearchTerm, setPropertySearchTerm] = useState(initialData.propertySearchTerm || '');
  const [selectedProperty, setSelectedProperty] = useState(() => {
    if (initialData.property?.id) return initialData.property;
    if (initialData.propertyId && initialData.propertySearchTerm) {
      return { id: initialData.propertyId, name: initialData.propertySearchTerm };
    }
    return null;
  });
  const [isPropertyDropdownOpen, setIsPropertyDropdownOpen] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  const initialPropertyId = initialData.propertyId;
  const initialPropertyName = initialData.propertySearchTerm;
  const initialPropertyObj = initialData.property;
  const initialPropertyObjId = initialPropertyObj?.id;
  const initialPropertyObjName = initialPropertyObj?.name;
  const initialPropertyObjAddress = initialPropertyObj?.address;

  const { loading, error, data: propertiesData } = useQuery(GET_PROPERTIES, {
    variables: {
      limit: ITEMS_PER_PAGE,
      page: 1,
      search: propertySearchTerm || undefined,
      startDate: new Date().toISOString().split('T')[0],
      endDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    },
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (initialPropertyId && !selectedProperty && data?.propertiesWithReservations?.properties) {
        const foundProperty = data.propertiesWithReservations.properties.find(p => p.id === initialPropertyId);
        if (foundProperty) setSelectedProperty(foundProperty);
      }
    }
  });

  const { data: reservationCandidatesData, loading: reservationCandidatesLoading } = useQuery(
    GET_LOST_FOUND_RESERVATION_CANDIDATES,
    {
      variables: { propertyId: selectedProperty?.id || '' },
      skip: !selectedProperty?.id,
      fetchPolicy: 'network-only',
    }
  );

  useEffect(() => {
    if (initialPropertyObjId) {
      setSelectedProperty({
        id: initialPropertyObjId,
        name: initialPropertyObjName,
        address: initialPropertyObjAddress,
      });
      setPropertySearchTerm(initialPropertyObjName);
    } else if (initialPropertyId && initialPropertyName) {
      setSelectedProperty(prev => prev || { id: initialPropertyId, name: initialPropertyName });
      setPropertySearchTerm(initialPropertyName);
    }
  }, [initialPropertyId, initialPropertyName, initialPropertyObjId, initialPropertyObjName, initialPropertyObjAddress]);

  const reservationCandidates = useMemo(
    () => reservationCandidatesData?.lostFoundReservationCandidates || [],
    [reservationCandidatesData]
  );

  useEffect(() => {
    if (initialData.reservationId) return;
    if (!reservationId && reservationCandidates.length > 0) {
      setReservationId(reservationCandidates[0].id);
    }
  }, [initialData.reservationId, reservationCandidates, reservationId]);

  /** Build the submission payload from current state, using selectedProperty as source of truth for propertyId */
  const buildSubmissionData = useCallback(() => ({
    propertyId: selectedProperty?.id || '',
    reservationId: reservationId === NO_RESERVATION_VALUE ? null : (reservationId || undefined),
    category,
    description,
    photos,
    returnOfficeInstructions,
  }), [selectedProperty, reservationId, category, description, photos, returnOfficeInstructions]);

  const validate = useCallback((data) => {
    const newErrors = {};
    if (!data.propertyId) {
      newErrors.propertyId = 'Property is required';
    } else if (!UUID_REGEX.test(data.propertyId)) {
      newErrors.propertyId = 'Invalid property format. Please select a valid property.';
    }
    if (!data.description?.trim()) {
      newErrors.description = 'Description is required';
    }
    if (!data.category) {
      newErrors.category = 'Category is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, []);

  const handleSubmit = useCallback((e) => {
    if (e?.preventDefault) e.preventDefault();
    const data = buildSubmissionData();
    if (validate(data)) onSubmit(data);
  }, [buildSubmissionData, validate, onSubmit]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'description') setDescription(value);
    if (name === 'returnOfficeInstructions') setReturnOfficeInstructions(value);
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handlePropertySelect = (property) => {
    if (!property?.id) return;
    setSelectedProperty(property);
    setPropertySearchTerm(property.name);
    setReservationId('');
    setIsPropertyDropdownOpen(false);
    if (errors.propertyId) {
      setErrors(prev => ({ ...prev, propertyId: null }));
    }
  };

  const handlePropertySearchChange = (e) => {
    setPropertySearchTerm(e.target.value);
    setIsPropertyDropdownOpen(true);
  };

  const handleClearProperty = () => {
    setSelectedProperty(null);
    setPropertySearchTerm('');
    setReservationId('');
  };

  const filteredProperties = useMemo(() => {
    const properties = propertiesData?.propertiesWithReservations?.properties || [];
    if (!propertySearchTerm.trim()) return properties;
    const search = propertySearchTerm.toLowerCase();
    return properties.filter(property =>
      property.name.toLowerCase().includes(search) ||
      property.address.toLowerCase().includes(search) ||
      property.city?.toLowerCase().includes(search) ||
      property.propertyCode?.toLowerCase().includes(search)
    );
  }, [propertiesData, propertySearchTerm]);

  const onDrop = async (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;

    setIsUploading(true);
    setUploadProgress(0);

    try {
      let processedFile = file;

      if (file.type === 'image/heic' || file.type === 'image/heif' ||
          file.name.toLowerCase().endsWith('.heic') || file.name.toLowerCase().endsWith('.heif')) {
        toast.loading("Converting iPhone HEIC image...", { id: "heic-conversion" });
        try {
          const heic2any = (await import('heic2any')).default;
          const jpegBlob = await heic2any({
            blob: file,
            toType: "image/jpeg",
            quality: 0.8,
            timeout: 30000
          });
          if (!jpegBlob) throw new Error('Conversion resulted in an empty file');
          processedFile = new File(
            [jpegBlob],
            file.name.replace(/\.(heic|heif)$/i, '.jpg'),
            { type: 'image/jpeg' }
          );
          toast.success("HEIC image converted successfully", { id: "heic-conversion" });
        } catch {
          toast.dismiss("heic-conversion");
          toast.error("Unable to convert HEIC image. Please convert it to JPEG or PNG before uploading.");
          setIsUploading(false);
          return;
        }
      }

      if (processedFile.size > MAX_FILE_SIZE) {
        toast.error("Image is too large. Maximum size is 10MB.");
        setIsUploading(false);
        return;
      }

      const previewUrl = URL.createObjectURL(processedFile);
      setImagePreview(previewUrl);
      setUploadProgress(20);

      const response = await fetch(`${API_URL}/api/lost-found/get-upload-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          fileName: processedFile.name,
          fileType: processedFile.type
        })
      });

      if (!response.ok) throw new Error(`Failed to get upload URL: ${response.statusText}`);

      const { uploadUrl, imageUrl, headers } = await response.json();
      setUploadProgress(50);

      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        headers: headers,
        body: processedFile
      });

      if (!uploadResponse.ok) throw new Error(`Failed to upload file: ${uploadResponse.statusText}`);

      setUploadProgress(90);
      setPhotos(prev => [...prev, imageUrl]);
      setUploadProgress(100);
      toast.success('Image uploaded successfully');
    } catch {
      toast.error('Failed to upload image. Please try again.');
    } finally {
      setIsUploading(false);
      setImagePreview(null);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.heic', '.heif', '.webp']
    },
    maxFiles: 1,
    multiple: false
  });

  const handleRemovePhoto = (indexToRemove) => {
    setPhotos(prev => prev.filter((_, index) => index !== indexToRemove));
  };

  useImperativeHandle(ref, () => ({
    submitForm: () => {
      const data = buildSubmissionData();
      if (validate(data)) {
        onSubmit(data);
        return true;
      }
      return false;
    }
  }), [buildSubmissionData, validate, onSubmit]);

  const isPrefilledProperty = initialPropertyId && initialPropertyName && hideButtons && selectedProperty;

  return (
    <form id="lost-found-form" onSubmit={handleSubmit} className="space-y-6">
      {/* Property Selection */}
      <div>
        <label htmlFor="property" className="block text-sm font-medium text-gray-700">
          Property *
        </label>
        <div className="mt-1 relative">
          {isPrefilledProperty ? (
            <div className="mt-2 flex items-center bg-blue-50 rounded-md p-3">
              <span className="text-sm text-blue-800 flex-grow font-medium">
                {selectedProperty.name || initialPropertyName}
                {selectedProperty.address && ` - ${selectedProperty.address}`}
              </span>
            </div>
          ) : (
            <>
              <input
                type="text"
                id="property"
                name="property"
                value={propertySearchTerm}
                onChange={handlePropertySearchChange}
                onFocus={() => setIsPropertyDropdownOpen(true)}
                onBlur={() => {
                  setTimeout(() => {
                    setIsPropertyDropdownOpen(false);
                  }, 200);
                }}
                placeholder="Search for a property"
                className={`block w-full rounded-md shadow-sm px-3 py-2 ${
                  errors.propertyId
                    ? 'border border-red-300 focus:ring-red-500 focus:border-red-500'
                    : 'border border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                }`}
              />
              {loading && (
                <div className="absolute right-3 top-2">
                  <div className="animate-spin h-5 w-5 border-2 border-blue-500 rounded-full border-t-transparent"></div>
                </div>
              )}
              {selectedProperty && (
                <div className="mt-2 flex items-center bg-blue-50 rounded-md p-2">
                  <span className="text-sm text-blue-800 flex-grow">
                    {selectedProperty.name} - {selectedProperty.address}
                  </span>
                  <button
                    type="button"
                    onClick={handleClearProperty}
                    className="text-blue-500 hover:text-blue-700"
                    aria-label="Clear property selection"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              )}
            </>
          )}
          {errors.propertyId && (
            <p className="mt-1 text-sm text-red-600">{errors.propertyId}</p>
          )}
          {isPropertyDropdownOpen && (
            <div className="absolute z-10 mt-1 w-full bg-white shadow-lg rounded-md max-h-60 overflow-auto">
              {error ? (
                <div className="p-4 text-red-500">Error loading properties</div>
              ) : loading ? (
                <div className="p-4 text-gray-500">Loading properties...</div>
              ) : filteredProperties.length > 0 ? (
                <ul className="py-1 text-base ring-1 ring-black ring-opacity-5" role="listbox">
                  {filteredProperties.map((property) => (
                    <li
                      key={property.id}
                      onMouseDown={() => handlePropertySelect(property)}
                      className="cursor-pointer hover:bg-gray-100 px-4 py-2 text-sm text-gray-900"
                      role="option"
                      aria-selected={selectedProperty?.id === property.id}
                    >
                      <div className="font-medium">{property.name}</div>
                      <div className="text-xs text-gray-500">
                        {property.address}
                        {property.city && `, ${property.city}`}
                        {property.state && `, ${property.state}`}
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="p-4 text-gray-500">No properties found</div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Reservation Selection */}
      {selectedProperty && (
        <div>
          <label htmlFor="reservation" className="block text-sm font-medium text-gray-700">
            Reservation
          </label>
          <div className="mt-1">
            <select
              id="reservation"
              name="reservation"
              value={reservationId}
              onChange={(e) => setReservationId(e.target.value)}
              className="block w-full rounded-md border border-gray-300 shadow-sm px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={reservationCandidatesLoading}
            >
              <option value="" disabled>
                {reservationCandidatesLoading ? 'Loading reservations...' : 'Select a reservation'}
              </option>
              <option value={NO_RESERVATION_VALUE}>No reservation / Unknown</option>
              {reservationCandidates.map((reservation) => (
                <option key={reservation.id} value={reservation.id}>
                  {reservation.label}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Shows the current reservation and the previous ten reservations for this property.
            </p>
          </div>
        </div>
      )}

      {/* Category */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Category *
        </label>
        <div className="space-y-2" role="radiogroup" aria-label="Lost and found category">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="radio"
              name="category"
              value={LOST_FOUND_CATEGORY.LOST_ITEM_FOUND}
              checked={category === LOST_FOUND_CATEGORY.LOST_ITEM_FOUND}
              onChange={() => setCategory(LOST_FOUND_CATEGORY.LOST_ITEM_FOUND)}
              className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-900">Lost Item Found</span>
            <span className="text-xs text-gray-500">(Item was found by staff)</span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="radio"
              name="category"
              value={LOST_FOUND_CATEGORY.REPORTED_LOST_ITEM}
              checked={category === LOST_FOUND_CATEGORY.REPORTED_LOST_ITEM}
              onChange={() => setCategory(LOST_FOUND_CATEGORY.REPORTED_LOST_ITEM)}
              className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-900">Reported Lost Item</span>
            <span className="text-xs text-gray-500">(Guest reported an item lost)</span>
          </label>
        </div>
        {errors.category && (
          <p className="mt-1 text-sm text-red-600">{errors.category}</p>
        )}
      </div>

      {/* Return Instructions */}
      <div>
        <label htmlFor="returnOfficeInstructions" className="block text-sm font-medium text-gray-700">
          Return Instructions
        </label>
        <div className="mt-1">
          <input
            id="returnOfficeInstructions"
            name="returnOfficeInstructions"
            type="text"
            value={returnOfficeInstructions}
            onChange={handleInputChange}
            className="block w-full rounded-md border border-gray-300 shadow-sm px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Return this item to the correct office."
          />
        </div>
      </div>

      {/* Description */}
      <div>
        <label htmlFor="description" className="block text-sm font-medium text-gray-700">
          Description *
        </label>
        <div className="mt-1">
          <textarea
            id="description"
            name="description"
            rows={5}
            value={description}
            onChange={handleInputChange}
            className={`block w-full rounded-md shadow-sm px-3 py-2 ${
              errors.description
                ? 'border border-red-300 focus:ring-red-500 focus:border-red-500'
                : 'border border-gray-300 focus:ring-blue-500 focus:border-blue-500'
            }`}
            placeholder="Describe the lost item in detail"
          />
          {errors.description && (
            <p className="mt-1 text-sm text-red-600">{errors.description}</p>
          )}
        </div>
      </div>

      {/* Photos Upload */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Photos
        </label>
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
            isDragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
          }`}
        >
          <input {...getInputProps()} />
          {imagePreview ? (
            <div className="space-y-2">
              <SafeImage
                src={imagePreview}
                alt="Preview"
                className="mx-auto max-h-48 rounded-lg"
                lazy={false}
              />
              <p className="text-sm text-gray-500">
                Uploading...
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                stroke="currentColor"
                fill="none"
                viewBox="0 0 48 48"
                aria-hidden="true"
              >
                <path
                  d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <div className="text-sm text-gray-600">
                <span className="text-blue-600 hover:text-blue-500">
                  Click to upload
                </span>{' '}
                or drag and drop
              </div>
              <p className="text-xs text-gray-500">
                PNG, JPG, GIF, HEIC (iPhone) up to 10MB
              </p>
            </div>
          )}
        </div>
        {isUploading && (
          <div className="mt-2">
            <div className="bg-gray-200 rounded-full h-2.5">
              <div
                className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
          </div>
        )}

        {/* Photo Gallery */}
        {photos.length > 0 && (
          <div className="mt-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Uploaded Photos</h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {photos.map((photo, index) => (
                <div key={index} className="relative group">
                  <SafeImage
                    src={photo}
                    alt={`Lost item photo ${index + 1}`}
                    className="h-24 w-full object-cover rounded-lg"
                    lazy={false}
                  />
                  <button
                    type="button"
                    onClick={() => handleRemovePhoto(index)}
                    className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    aria-label="Remove photo"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Only show buttons if hideButtons is false */}
      {!hideButtons && (
        <div className="flex justify-end space-x-3 pt-5">
          <button
            type="button"
            onClick={() => window.history.back()}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className={`px-4 py-2 rounded-md text-sm font-medium text-white focus:outline-none ${
              isLoading
                ? 'bg-blue-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {isLoading ? 'Saving...' : 'Submit Lost Item'}
          </button>
        </div>
      )}
    </form>
  );
});

export default LostFoundForm;
